import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';

const AdminDashboard = () => {
  const [wards, setWards] = useState([]);
  const [selectedWard, setSelectedWard] = useState(null);
  const [roads, setRoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const isAdmin = localStorage.getItem('isAdmin') === 'true';
      
      if (!isAdmin) {
        // If not admin, redirect to login
        navigate('/admin/login');
        return;
      }
      
      // Verify admin status with Supabase
      const { data: session } = await supabase.auth.getSession();
      if (!session || !session.session) {
        // No active session
        localStorage.removeItem('isAdmin');
        navigate('/admin/login');
        return;
      }
      
      // Check if user is in admin_users table
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('id')
        .eq('id', session.session.user.id)
        .single();
        
      if (adminError || !adminData) {
        // Not an admin
        localStorage.removeItem('isAdmin');
        navigate('/admin/login');
        return;
      }
      
      // Fetch wards data
      fetchWards();
    };
    
    checkAdmin();
  }, [navigate]);

  const fetchWards = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('wards')
        .select('*')
        .order('name');
        
      if (error) {
        throw error;
      }
      
      setWards(data || []);
    } catch (error) {
      console.error('Error fetching wards:', error);
      setError('Failed to load wards data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoadsForWard = async (wardId) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('roads')
        .select('*')
        .eq('ward_id', wardId)
        .order('name');
        
      if (error) {
        throw error;
      }
      
      setRoads(data || []);
      setSelectedWard(wardId);
    } catch (error) {
      console.error('Error fetching roads:', error);
      setError('Failed to load roads data.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('isAdmin');
      navigate('/admin/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const resetSelection = async (roadId) => {
    if (window.confirm('Are you sure you want to reset this road selection?')) {
      try {
        const { error } = await supabase
          .from('roads')
          .update({ 
            available: true,
            volunteer_name: null,
            volunteer_email: null,
            volunteer_phone: null 
          })
          .eq('id', roadId);
        
        if (error) {
          throw error;
        }
        
        // Refresh the roads data
        if (selectedWard) {
          fetchRoadsForWard(selectedWard);
        }
        
      } catch (error) {
        console.error('Error resetting road:', error);
        setError('Failed to reset road selection. Please try again.');
      }
    }
  };

  if (loading && wards.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00bed6]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-[#00bed6]">Admin Dashboard</h1>
        <div className="flex items-center space-x-4">
          <Link to="/deliveryplanner" className="text-[#00bed6] hover:text-[#009eb3]">
            Public View
          </Link>
          <button 
            onClick={handleLogout}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
          >
            Logout
          </button>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <h2 className="text-xl font-semibold mb-4">Select a Ward</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {wards.map(ward => (
            <button
              key={ward.id}
              onClick={() => fetchRoadsForWard(ward.id)}
              className={`p-3 rounded-lg text-center transition-colors ${
                selectedWard === ward.id
                  ? 'bg-[#00bed6] text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              }`}
            >
              {ward.name}
            </button>
          ))}
        </div>
      </div>
      
      {selectedWard && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <h2 className="text-xl font-semibold mb-4">
            {wards.find(w => w.id === selectedWard)?.name} - Road Assignments
          </h2>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00bed6]"></div>
            </div>
          ) : roads.length === 0 ? (
            <p className="text-center py-4 text-gray-600">No roads found in this ward.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Road</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Houses</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volunteer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {roads.map(road => (
                    <tr key={road.id} className={!road.available ? 'bg-green-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{road.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{road.houses || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {road.available ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Available
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Reserved
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {road.volunteer_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(road.volunteer_email && (
                          <div>
                            <div>{road.volunteer_email}</div>
                            <div>{road.volunteer_phone}</div>
                          </div>
                        )) || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {!road.available && (
                          <button 
                            onClick={() => resetSelection(road.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Reset
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      <footer className="mt-12 text-center text-sm text-gray-500">
        <p className="mt-1"> 2025 Reform UK - Welwyn Hatfield</p>
      </footer>
    </div>
  );
};

export default AdminDashboard;
